import _get from "lodash/get"
import { isArray } from "lodash"
import { store } from "@/store"
import { getCountryName, getStateName, validateUSPhone } from "@/js/helperV2"

import {
  validateInputField,
  validateZip,
  getFieldValidationMessage,
} from "@/utils/location"
import { removeExtraSpaces } from "@/utils/"

import { setAuthModalVisibility } from "@/store/features/authSlice"
import {
  discount,
  taxCalculation,
  addToCart,
  createCart,
  setCardStatus,
} from "@/store/features/cartSlice"
import { getConfig } from "@/constants/config"
import { getCurrency, getShortenedUrlPath } from "@/utils/helper"

import { isValidInputData } from "@/components/checkout/v2/utils/checkout"

export const dispatchCallbackHandler = (type, payload = null) => {
  switch (type) {
    case "set-auth-modal": {
      return store.dispatch(setAuthModalVisibility(payload))
    }
    case "discount": {
      return store.dispatch(discount(payload))
    }
    case "taxCalculation": {
      return store.dispatch(taxCalculation())
    }
  }
}

export const redirectToCartPage = () => {
  getConfig().then(config => {
    const { general: { cartLandingPagePath = "en/cart" } = {} } = config || {}
    const cartLandingPageUrl = getShortenedUrlPath(cartLandingPagePath)
    window.location.href = cartLandingPageUrl
  })
}

let cartLoadEpoch = 0

export const initialCartLoad = (data, logger = () => {}) => {
  const { getZipCode, onError, force = false } = data
  const { cart: { isCartLoading } = {} } = store.getState()

  if (isCartLoading && !force) return

  // A superseded load still resolves; gate its handlers so they cannot redirect.
  const epoch = ++cartLoadEpoch
  const isCurrentCartLoad = () => epoch === cartLoadEpoch
  const clearLoadingForCurrentLoad = () => {
    if (isCurrentCartLoad()) store.dispatch(setCardStatus(false))
  }

  store.dispatch(setCardStatus(true))
  const payload = {
    actions: [
      {
        action: "recalculate",
        updateProductData: true,
      },
    ],
  }

  store
    .dispatch(addToCart(payload))
    .unwrap()
    .then(rsp => {
      if (!isCurrentCartLoad()) return

      if (
        rsp?.cartExists &&
        rsp?.cartExists === "false" &&
        rsp?.error === "No active cart exists."
      ) {
        getCurrency().then(currency => {
          if (!isCurrentCartLoad()) return
          const cartPayload = {
            currency: currency,
            taxMode: "ExternalAmount",
          }
          store
            .dispatch(createCart(cartPayload))
            .unwrap()
            .then(rsp => {
              if (!isCurrentCartLoad()) return
              getZipCode(rsp)
              store.dispatch(setCardStatus(false))
              redirectToCartPage()
            })
            .catch(error => {
              clearLoadingForCurrentLoad()
              logger?.error("Error creating cart", { error })
              if (isCurrentCartLoad() && onError) {
                onError()
              }
            })
        })
      } else if (!rsp?.lineItems.length) {
        // Handle the case where the no line item in cart
        store.dispatch(setCardStatus(false))
        redirectToCartPage()
      } else {
        store.dispatch(setCardStatus(false))
      }
    })
    .catch(error => {
      clearLoadingForCurrentLoad()
      logger?.error("Error adding to cart", { error })
      if (isCurrentCartLoad() && onError) {
        onError()
      }
    })
}

export const recalculateCart = () => {
  store.dispatch(setCardStatus(false))
  const payload = {
    actions: [
      {
        action: "recalculate",
        updateProductData: true,
      },
    ],
  }
  store.dispatch(addToCart(payload))
}

export const getAddressData = (data = {}, isLabel1) => {
  const { item = {}, i18n = {}, defaultId, tileIndex } = data

  const label1 = { value: item?.label ?? "" }

  const label2 = {
    show: item.id === defaultId,
    value: _get(item, "custom.fields.label", `${i18n.address} #${tileIndex}`),
  }

  const label = isLabel1 ? label1 : label2

  return {
    label,
    fname: { value: item.firstName ?? "" },
    lname: { value: item.lastName ?? "" },
    email: { value: item.email ?? "" },
    phone: { value: item.phone ?? "" },
    address1: { value: item.streetName ?? "" },
    address2: item.streetNumber ?? "",
    city: { value: item.city ?? "" },
    state: { value: item.state ?? "" },
    zipcode: { value: item.postalCode ?? "" },
    country: { value: item.country ?? "" },
    id: item?.id ?? "",
  }
}

export const getFilteredData = (suggData = [], excludedAddresses = []) => {
  return suggData.filter(
    item => !excludedAddresses.some(address => item.startsWith(address))
  )
}

export const getFilteredAddress = (suggData = null) => {
  const {
    generic: {
      globalConfig: {
        regexPattern: { map: { excludePOBoxAddress = "" } = {} } = {},
      } = {},
    },
  } = store.getState()

  if (excludePOBoxAddress) {
    const poBoxRegex = new RegExp(excludePOBoxAddress, "i")
    if (isArray(suggData)) {
      return suggData.filter(item => !poBoxRegex.test(item))
    } else if (typeof suggData === "string") {
      return !poBoxRegex.test(suggData)
    }
  }
  return suggData
}

export const isPOBoxAddress = address => {
  if (typeof address !== "string") return false

  const trimmedAddress = removeExtraSpaces(address)
  if (!trimmedAddress) return false

  return !getFilteredAddress(trimmedAddress)
}

export const getDefaultFormData = data => {
  const { props, isAuth, user } = data
  return {
    fname: {
      value: _get(props.data, "fname.value", ""),
      error: false,
      message: "",
    },
    lname: {
      value: _get(props.data, "lname.value", ""),
      error: false,
      message: "",
    },
    email: {
      value:
        _get(props.data, "email.value", "") ||
        (isAuth && !user?.koc ? user?.sub : ""),
      error: false,
      message: "",
    },
    phone: {
      value: _get(props.data, "phone.value", ""),
      error: false,
      message: "",
    },
    address1: {
      value: _get(props.data, "address1.value", ""),
      error: false,
      message: "",
    },
    address2: _get(props.data, "address2", ""),
    city: {
      value: _get(props.data, "city.value", ""),
      error: false,
      message: "",
    },
    state: {
      value: getStateName(
        _get(props.data, "state.value", ""),
        _get(props.data, "country.value", props.country ?? ""),
        props.countries
      ),
      error: false,
      message: "",
    },
    zipcode: {
      value: _get(props.data, "zipcode.value", ""),
      error: false,
      message: "",
    },
    country: {
      value: getCountryName(
        _get(props.data, "country.value", props.country ?? ""),
        props.countries
      ),
      error: false,
      message: "",
    },
    notes: "",
    label: {
      show: _get(props.data, "label.show", true),
      value: _get(props.data, "label.value", ""),
    },
    id: _get(props.data, "id", ""),
    password: {
      value: _get(props.data, "password.value", ""),
      error: false,
      message: "",
    },
    companyName: {
      value: _get(props.data, "companyName.value", ""),
      error: false,
      message: "",
    },
    accountType: {
      value: _get(props.data, "accountType.value", ""),
      error: false,
      message: "",
    },
    businessType: {
      value: _get(props.data, "businessType.value", ""),
      error: false,
      message: "",
    },
  }
}

// Validates all required fields in address using isValidInputData from checkout.js
const validateAllAddressFields = (addr, requiredFields) => {
  for (const field of requiredFields) {
    const value = addr[field]?.value
    if (!isValidInputData(field, value)) {
      return { isValid: false, invalidField: field }
    }
  }
  return { isValid: true }
}

export const checkDisabled = data => {
  const {
    addr,
    type,
    isSignUp = false,
    isPasswordValid = {},
    createAccountType = "",
  } = data

  // Check if any field has error flag set
  const hasFormAnyError = Object.values(addr).some(item => item?.error === true)
  if (hasFormAnyError) return true

  const accountType = createAccountType.toLowerCase()
  const isProfessional = accountType.includes("professional")
  const isHomeowner = accountType.includes("homeowner")

  // Define required fields based on context
  const baseFields = ["phone", "address1", "city", "state", "zipcode"]
  const commonFields = ["fname", "lname", "email", ...baseFields]

  // Edit/Add mode - only need base address fields
  if ((type === "edit" || type === "add") && !isSignUp) {
    const password = addr.password?.value?.trim()
    if (password) return true // shouldn't have password in edit/add mode
    return !validateAllAddressFields(addr, baseFields).isValid
  }

  // Guest checkout - no signup
  if (!isSignUp) {
    return !validateAllAddressFields(addr, commonFields).isValid
  }

  // Signup flow
  const password = addr.password?.value?.trim()
  const isPasswordOk = password && isPasswordValid?.valid !== false
  if (!isPasswordOk) return true

  if (isProfessional && !isHomeowner) {
    // Professional account needs companyName and businessType
    const proFields = [
      ...commonFields,
      "password",
      "companyName",
      "businessType",
    ]
    return !validateAllAddressFields(addr, proFields).isValid
  }

  if (isHomeowner && !isProfessional) {
    // Homeowner account
    const homeFields = [...commonFields, "password"]
    return !validateAllAddressFields(addr, homeFields).isValid
  }

  return true
}
// getValidationData this function is not used in anywhere don't do the code changes
export const getValidationData = data => {
  const { i18n, formData, type, setCtaLoad, isSignUpForm = false } = data
  const obj = {}
  let valid = true
  const errorMessage = []
  const isBingActive = process.env.NEXT_PUBLIC_CHECKOUT_BING === "enabled"
  setCtaLoad && setCtaLoad(true)

  if (type !== "add" && type !== "edit") {
    if (!formData.email.value) {
      obj.email = {
        ...formData.email,
        error: true,
        message: i18n.noEmailError,
      }
      valid = false
      errorMessage.push(i18n.noEmailError)
    } else if (!validateInputField("email", formData.email.value)) {
      obj.email = { ...formData.email, error: true, message: i18n.validEmail }
      valid = false
      errorMessage.push(i18n.validEmail)
    }
  }
  if (!validateUSPhone(formData.phone.value)) {
    obj.phone = { ...formData.phone, error: true, message: i18n.validUSPhone }
    valid = false
    errorMessage.push(i18n.validUSPhone)
  }
  if (!formData.fname.value?.trim()) {
    obj.fname = { ...formData.fname, error: true, message: i18n.fnameError }
    valid = false
    errorMessage.push(i18n.fnameError)
  } else if (!validateInputField("name", formData.fname.value)) {
    obj.fname = {
      ...formData.fname,
      error: true,
      message: i18n.nameValidation,
    }
    valid = false
    errorMessage.push(i18n.nameValidation)
  }
  if (!formData.lname.value?.trim()) {
    obj.lname = { ...formData.lname, error: true, message: i18n.lnameError }
    valid = false
    errorMessage.push(i18n.lnameError)
  } else if (!validateInputField("name", formData.lname.value)) {
    obj.lname = {
      ...formData.lname,
      error: true,
      message: i18n.nameValidation,
    }
    valid = false
    errorMessage.push(i18n.nameValidation)
  }
  if (!formData.address1.value?.trim()) {
    obj.address1 = {
      ...formData.address1,
      error: true,
      message: i18n.addressError,
    }
    valid = false
    errorMessage.push(i18n.addressError)
  }
  if (!formData.password.value?.trim() && isSignUpForm) {
    obj.password = {
      ...formData.password,
      error: true,
      message: i18n.pwdErrorText,
    }
    valid = false
    errorMessage.push(i18n.pwdErrorText)
  }
  if (!formData.city.value?.trim()) {
    obj.city = { ...formData.city, error: true, message: i18n.cityError }
    valid = false
    errorMessage.push(i18n.cityError)
  } else if (!validateInputField("city", formData.city.value)) {
    obj.city = { ...formData.city, error: true, message: i18n.cityValidation }
    valid = false
    errorMessage.push(i18n.cityValidation)
  }
  if (!formData.state.value) {
    obj.state = { ...formData.state, error: true, message: i18n.stateError }
    valid = false
    errorMessage.push(i18n.stateError)
  }
  if (!formData.zipcode.value?.trim()) {
    obj.zipcode = {
      ...formData.zipcode,
      error: true,
      message: i18n.zipcodeError,
    }
    valid = false
    errorMessage.push(i18n.zipcodeError)
  } else if (formData.zipcode.value.trim().length < 5) {
    obj.zipcode = {
      ...formData.zipcode,
      error: true,
      message: i18n.validZipcode,
    }
    valid = false
    errorMessage.push(i18n.validZipcode)
  } else if (isBingActive) {
    if (!validateZip(formData.zipcode.value)) {
      obj.zipcode = {
        ...formData.zipcode,
        error: true,
        message: i18n.validZipcode,
      }
      valid = false
      errorMessage.push(i18n.validZipcode)
    }
  }
  if (formData?.label?.error) {
    valid = false
    errorMessage.push(i18n.nameValidation)
  }

  return { valid, validationData: obj, errorMessage }
}

export const getAddressString = addressData => {
  const {
    streetName = "",
    streetNumber = "",
    city = "",
    state = "",
    postalCode = "",
  } = addressData

  const addressStreetName = removeExtraSpaces(streetName)
  const addressStreetNumber = removeExtraSpaces(streetNumber)
  const streetLine =
    addressStreetName && addressStreetNumber
      ? `${addressStreetName} ${addressStreetNumber}`
      : addressStreetName

  return `${streetLine}, ${removeExtraSpaces(city)} ${removeExtraSpaces(
    state
  )} ${removeExtraSpaces(postalCode)}`
}

export const filterComboChildForAnalytics = cartData => {
  if (!cartData?.cartItems || !cartData?.cart?.item) {
    return { filteredCartItems: [], filteredCart: [] }
  }

  // Filter out combo child skus
  const filteredCartItems = cartData.cartItems
    .filter(item => !item.bundleParentId)
    .map(item => item?.customerFacingSKU)
    .filter(Boolean)

  const filteredCart = cartData.cart.item.filter(item =>
    filteredCartItems.includes(item?.productInfo?.productID)
  )

  return {
    filteredCartItems,
    filteredCart,
  }
}
export const checkAddressFormDisabled = data => {
  const { addr = {} } = data
  const {
    phone: { value: phoneValue = "" } = {},
    address1: { value: address1Value = "" } = {},
    city: { value: cityValue = "" } = {},
    state: { value: stateValue = "" } = {},
    zipcode: { value: zipcodeValue = "", error: zipcodeError = false } = {},
  } = addr || {}

  const phone = phoneValue.trim()
  const address1 = address1Value.trim()
  const city = cityValue.trim()
  const state = stateValue.trim()
  const zipcode = zipcodeValue.trim()
  const isValidZipcode = zipcode && !zipcodeError && zipcode.length >= 5

  const hasFormAnyError = Object.values(addr).some(item => item?.error === true)
  if (hasFormAnyError) {
    return true
  }

  if (phone && address1 && city && state && isValidZipcode) {
    return false
  } else return true
}

export const getAddressValidationData = data => {
  const { i18n, formData, setCtaLoad } = data
  const obj = {}
  let valid = true
  const errorMessage = []
  const isBingActive = process.env.NEXT_PUBLIC_CHECKOUT_BING === "enabled"
  setCtaLoad && setCtaLoad(true)
  if (!validateUSPhone(formData.phone.value)) {
    obj.phone = { ...formData.phone, error: true, message: i18n.validUSPhone }
    valid = false
    errorMessage.push(i18n.validUSPhone)
  }
  if (!formData.address1.value?.trim()) {
    obj.address1 = {
      ...formData.address1,
      error: true,
      message: i18n.addressError,
    }
    valid = false
    errorMessage.push(i18n.addressError)
  }
  if (!formData.city.value?.trim()) {
    obj.city = { ...formData.city, error: true, message: i18n.cityError }
    valid = false
    errorMessage.push(i18n.cityError)
  } else if (!validateInputField("city", formData.city.value)) {
    obj.city = { ...formData.city, error: true, message: i18n.cityValidation }
    valid = false
    errorMessage.push(i18n.cityValidation)
  }
  if (!formData.state.value) {
    obj.state = { ...formData.state, error: true, message: i18n.stateError }
    valid = false
    errorMessage.push(i18n.stateError)
  }
  if (!formData.zipcode.value?.trim()) {
    obj.zipcode = {
      ...formData.zipcode,
      error: true,
      message: i18n.zipcodeError,
    }
    valid = false
    errorMessage.push(i18n.zipcodeError)
  } else if (formData.zipcode.value.trim().length < 5) {
    obj.zipcode = {
      ...formData.zipcode,
      error: true,
      message: i18n.validZipcode,
    }
    valid = false
    errorMessage.push(i18n.validZipcode)
  } else if (isBingActive) {
    if (!validateZip(formData.zipcode.value)) {
      obj.zipcode = {
        ...formData.zipcode,
        error: true,
        message: i18n.validZipcode,
      }
      valid = false
      errorMessage.push(i18n.validZipcode)
    }
  }
  if (formData?.label?.error) {
    valid = false
    errorMessage.push(i18n.nameValidation)
  }
  return { valid, validationData: obj, errorMessage }
}

export const checkContactFormDisabled = data => {
  const {
    formData = {},
    isCreateAccount = false,
    isAuth = false,
    isPasswordValid = {},
  } = data

  const {
    email: { value: emailValue = "" } = {},
    fname: { value: fnameValue = "" } = {},
    lname: { value: lnameValue = "" } = {},
    password: { value: passwordValue = "" } = {},
    selectedAccount = "",
    businessType: { value: businessTypeValue = "" } = {},
  } = formData

  const email = emailValue.trim()
  const fname = fnameValue.trim()
  const lname = lnameValue.trim()
  const password = passwordValue.trim()
  const businessType = businessTypeValue.trim()

  const hasFormAnyError = Object.values(formData).some(
    item => item?.error === true || item?.message
  )
  if (hasFormAnyError) {
    return true
  }

  const professionalAccount = selectedAccount
    ?.toLowerCase()
    .includes("professional")
  const homeOwnerAccount = selectedAccount?.toLowerCase().includes("homeowner")

  const basicFields = fname && lname && email

  if (basicFields && (!isCreateAccount || isAuth)) {
    return false
  }

  // Create account - Professional
  if (
    basicFields &&
    professionalAccount &&
    !homeOwnerAccount &&
    password &&
    isPasswordValid?.valid === true &&
    isCreateAccount &&
    !isAuth &&
    businessType
  ) {
    return false
  }

  // Create account - Homeowner
  if (
    basicFields &&
    homeOwnerAccount &&
    !professionalAccount &&
    password &&
    isPasswordValid?.valid === true &&
    isCreateAccount &&
    !isAuth
  ) {
    return false
  }

  return true
}

const CHECKOUT_GENERIC_ERROR_KEYS = {
  noEmail: "NO_EMAIL_ERROR",
  firstName: "FIRST_NAME_ERROR",
  lastName: "LAST_NAME_ERROR",
  pwdErrorText: "EMPTY_PWD_ERROR",
  businessType: "BUSINESS_TYPE_ERROR",
}

const resolveGenericFieldMessage = (
  genericMessages = [],
  key = "",
  fallback = ""
) => genericMessages?.find(msg => msg.text === key)?.value || fallback

export const validateContactForm = data => {
  const {
    i18n = {},
    formData = {},
    genericMessages = [],
    isCreateAccount = false,
    isAuth = false,
  } = data
  const obj = {}
  let valid = true
  const errorMessage = []

  const {
    email: { value: emailValue = "" } = {},
    fname: { value: fnameValue = "" } = {},
    lname: { value: lnameValue = "" } = {},
    password: { value: passwordValue = "" } = {},
    selectedAccount = "",
    businessType: { value: businessTypeValue = "" } = {},
  } = formData

  if (!emailValue?.trim()) {
    const emailMessage = resolveGenericFieldMessage(
      genericMessages,
      CHECKOUT_GENERIC_ERROR_KEYS.noEmail
    )
    obj.email = {
      ...formData.email,
      message: emailMessage,
    }
    valid = false
    errorMessage.push(emailMessage)
  } else if (!validateInputField("email", emailValue)) {
    const emailValidationMessage =
      getFieldValidationMessage("email") || i18n.validEmail

    obj.email = {
      ...formData.email,
      message: emailValidationMessage,
    }
    valid = false
    errorMessage.push(emailValidationMessage)
  }

  if (!fnameValue?.trim()) {
    const firstNameMessage = resolveGenericFieldMessage(
      genericMessages,
      CHECKOUT_GENERIC_ERROR_KEYS.firstName
    )
    obj.fname = {
      ...formData.fname,
      message: firstNameMessage,
    }
    valid = false
    errorMessage.push(firstNameMessage)
  } else if (!validateInputField("name", fnameValue)) {
    const nameValidationMessage =
      getFieldValidationMessage("name") || i18n.nameValidation

    obj.fname = {
      ...formData.fname,
      message: nameValidationMessage,
    }
    valid = false
    errorMessage.push(nameValidationMessage)
  }

  if (!lnameValue?.trim()) {
    const lastNameMessage = resolveGenericFieldMessage(
      genericMessages,
      CHECKOUT_GENERIC_ERROR_KEYS.lastName
    )
    obj.lname = {
      ...formData.lname,
      message: lastNameMessage,
    }
    valid = false
    errorMessage.push(lastNameMessage)
  } else if (!validateInputField("name", lnameValue)) {
    const nameValidationMessage =
      getFieldValidationMessage("name") || i18n.nameValidation

    obj.lname = {
      ...formData.lname,
      message: nameValidationMessage,
    }
    valid = false
    errorMessage.push(nameValidationMessage)
  }

  if (isCreateAccount && !isAuth) {
    if (!passwordValue?.trim()) {
      const pwdErrorText = resolveGenericFieldMessage(
        genericMessages,
        CHECKOUT_GENERIC_ERROR_KEYS.pwdErrorText
      )
      obj.password = {
        ...formData.password,
        message: pwdErrorText,
      }
      valid = false
      errorMessage.push(pwdErrorText)
    }

    if (selectedAccount?.toLowerCase().includes("professional")) {
      if (!businessTypeValue?.trim()) {
        const businessTypeMessage = resolveGenericFieldMessage(
          genericMessages,
          CHECKOUT_GENERIC_ERROR_KEYS.businessType
        )
        obj.businessType = {
          ...formData.businessType,
          message: businessTypeMessage,
        }
        valid = false
        errorMessage.push(businessTypeMessage)
      }
    }
  }

  return { valid, validationData: obj, errorMessage }
}
